<template>
  <v-card elevation="2" class="mx-auto">
    <v-card-title class="text-h5 font-weight-light">
      <v-icon left color="primary">mdi-file-sign</v-icon>
      Editor de Firma Digital con DNIe
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pt-6">
      <v-row>
        <!-- Panel izquierdo: Archivos -->
        <v-col cols="12" md="4">
          <!-- PDF -->
          <v-file-input
            v-model="selectedFile"
            label="PDF a firmar"
            accept="application/pdf"
            prepend-icon="mdi-file-pdf-box"
            outlined
            dense
            show-size
            @change="onPdfChange"
            :disabled="isSigning"
          ></v-file-input>

          <!-- Tabs para imagen o dibujar -->
          <v-tabs v-model="signatureTab" class="mt-4">
            <v-tab>Subir Imagen</v-tab>
            <v-tab>Dibujar Firma</v-tab>
          </v-tabs>

          <v-tabs-items v-model="signatureTab">
            <!-- Tab: Subir imagen -->
            <v-tab-item>
              <v-file-input
                v-model="signatureImage"
                label="Imagen de tu firma"
                accept="image/png,image/jpeg"
                prepend-icon="mdi-image"
                outlined
                dense
                show-size
                @change="onSignatureChange"
                :disabled="isSigning"
                class="mt-4"
              ></v-file-input>

              <!-- Vista previa -->
              <div v-if="signatureImageUrl" class="mt-2">
                <div class="text-caption mb-1">Vista previa:</div>
                <img :src="signatureImageUrl" style="max-width: 100%; border: 1px solid #ddd; border-radius: 4px;" />
              </div>
            </v-tab-item>

            <!-- Tab: Dibujar firma -->
            <v-tab-item>
              <div class="mt-4">
                <s-signature-pad @save-signature="handleDrawnSignature" />
              </div>
            </v-tab-item>
          </v-tabs-items>

          <!-- DNI -->
          <v-text-field
            v-model="dni"
            :label="signatureType === 'digital' ? 'DNI (obligatorio para firma digital)' : 'DNI (opcional)'"
            :hint="signatureType === 'digital' ? 'Requerido para seleccionar automáticamente el certificado FIR del DNIe' : 'Para certificado automático (si se proporciona)'
            "
            persistent-hint
            outlined
            dense
            maxlength="12"
            prepend-icon="mdi-card-account-details"
            :disabled="isSigning"
            class="mt-4"
          ></v-text-field>

          <!-- Tipo de firma -->
          <v-radio-group
            v-model="signatureType"
            label="Tipo de firma"
            :disabled="isSigning"
            class="mt-4"
          >
            <v-radio
              label="Firma Electrónica (solo visual)"
              value="electronic"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Firma Electrónica</div>
                  <div class="text-caption">Solo agrega la imagen de firma al PDF (sin certificado digital)</div>
                </div>
              </template>
            </v-radio>
            <v-radio
              label="Firma Digital con DNIe"
              value="digital"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Firma Digital con DNIe</div>
                  <div class="text-caption">Firma criptográfica con certificado digital del DNIe</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-divider class="my-4"></v-divider>

          <!-- Botones de acción -->
          <v-btn
            color="primary"
            block
            @click="addSignature"
            :disabled="!signatureImageUrl || !pdfRendered"
            class="mb-2"
          >
            <v-icon left>mdi-plus</v-icon>
            Agregar Firma Electrónica
          </v-btn>

          <!-- Botón para agregar sello digital (solo en modo digital) -->
          <v-btn
            v-if="signatureType === 'digital'"
            color="red darken-2"
            dark
            block
            @click="addDigitalSignatureSeal"
            :disabled="!pdfRendered || digitalSignature !== null"
            class="mb-2"
          >
            <v-icon left>mdi-certificate</v-icon>
            {{ digitalSignature ? 'Sello Digital Agregado' : 'Agregar Sello Digital Visual' }}
          </v-btn>

          <!-- Sección de firma digital -->
          <div v-if="digitalSignature" class="mt-4">
            <v-card outlined class="pa-3">
              <div class="text-subtitle-2 mb-2 d-flex align-center">
                <v-icon color="red darken-2" left>mdi-certificate</v-icon>
                Sello Digital - Página {{ digitalSignature.page }}
              </div>
              <div class="text-caption mb-2">
                X: {{ Math.round(digitalSignature.x) }}, Y: {{ Math.round(digitalSignature.y) }}, 
                W: {{ Math.round(digitalSignature.width) }}, H: {{ Math.round(digitalSignature.height) }}
              </div>
              
              <v-switch
                v-model="digitalSignature.visible"
                label="Mostrar sello en PDF"
                dense
                hide-details
                class="mt-2"
              ></v-switch>

              <v-slider
                v-model="digitalSignature.width"
                label="Ancho"
                min="150"
                max="600"
                thumb-label
                class="mt-2"
              ></v-slider>

              <v-slider
                v-model="digitalSignature.height"
                label="Alto"
                min="30"
                max="150"
                thumb-label
              ></v-slider>

              <v-slider
                v-model="digitalSignature.rotation"
                label="Rotación"
                min="0"
                max="360"
                thumb-label
                prepend-icon="mdi-rotate-right"
              >
                <template v-slot:append>
                  <v-chip small>{{ digitalSignature.rotation }}°</v-chip>
                </template>
              </v-slider>

              <v-btn
                color="error"
                small
                block
                outlined
                @click="removeDigitalSignature"
                class="mt-2"
              >
                <v-icon small left>mdi-delete</v-icon>
                Eliminar Sello Digital
              </v-btn>
            </v-card>
          </div>

          <!-- Lista de firmas electrónicas agregadas -->
          <div v-if="signatures.length > 0" class="mt-4">
            <div class="text-subtitle-2 mb-2">Firmas agregadas ({{ signatures.length }}):</div>
            <v-list dense>
              <v-list-item
                v-for="(sig, index) in signatures"
                :key="index"
                :class="{ 'selected-signature': selectedSignatureIndex === index }"
                @click="selectSignature(index)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    Firma {{ index + 1 }} - Página {{ sig.page }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    X: {{ Math.round(sig.x) }}, Y: {{ Math.round(sig.y) }}, 
                    W: {{ Math.round(sig.width) }}, H: {{ Math.round(sig.height) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small @click.stop="duplicateSignature(index)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn icon small @click.stop="removeSignature(index)">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <!-- Controles de tamaño para firma seleccionada -->
          <div v-if="selectedSignatureIndex !== null" class="mt-4">
            <div class="text-subtitle-2 mb-2">Ajustar firma seleccionada:</div>
            <v-slider
              v-model="selectedSignature.width"
              label="Ancho"
              min="50"
              max="400"
              thumb-label
            ></v-slider>

            <v-slider
              v-model="selectedSignature.height"
              label="Alto"
              min="30"
              max="300"
              thumb-label
            ></v-slider>

            <v-slider
              v-model="selectedSignature.rotation"
              label="Rotación"
              min="0"
              max="360"
              thumb-label
              prepend-icon="mdi-rotate-right"
            >
              <template v-slot:append>
                <v-chip small>{{ selectedSignature.rotation }}°</v-chip>
              </template>
            </v-slider>

            <div class="d-flex gap-2 mt-2">
              <v-btn small outlined @click="selectedSignature.rotation = 0">
                <v-icon small left>mdi-restore</v-icon>
                0°
              </v-btn>
              <v-btn small outlined @click="selectedSignature.rotation = 90">
                90°
              </v-btn>
              <v-btn small outlined @click="selectedSignature.rotation = 180">
                180°
              </v-btn>
              <v-btn small outlined @click="selectedSignature.rotation = 270">
                270°
              </v-btn>
            </div>
          </div>
        </v-col>

        <!-- Panel derecho: Vista previa PDF con firmas draggables -->
        <v-col cols="12" md="8">
          <div class="d-flex align-center mb-2">
            <div class="text-subtitle-2">
              Vista previa del PDF
              <span v-if="totalPages > 1" class="ml-2">
                (Página {{ currentPage }} de {{ totalPages }})
              </span>
            </div>
            <v-spacer></v-spacer>
            <!-- Navegación de páginas -->
            <div v-if="totalPages > 1" class="d-flex align-center">
              <v-btn
                small
                icon
                @click="previousPage"
                :disabled="currentPage <= 1"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="mx-2">Página</span>
              <v-text-field
                v-model.number="pageInput"
                type="number"
                min="1"
                :max="totalPages"
                dense
                outlined
                hide-details
                style="width: 60px"
                @keyup.enter="goToPage"
                @blur="goToPage"
              ></v-text-field>
              <span class="mx-2">de {{ totalPages }}</span>
              <v-btn
                small
                icon
                @click="nextPage"
                :disabled="currentPage >= totalPages"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Contenedor del canvas PDF con firmas draggables -->
          <div
            ref="pdfContainer"
            class="pdf-container"
            @mousemove="onMouseMove"
            @mouseup="onMouseUp"
            @mouseleave="onMouseUp"
          >
            <canvas ref="pdfCanvas"></canvas>

            <!-- Múltiples firmas electrónicas draggables -->
            <div
              v-for="(sig, index) in currentPageSignatures"
              :key="'sig-' + index"
              class="signature-overlay"
              :class="{ 'signature-selected': selectedSignatureIndex === signatures.indexOf(sig) }"
              :style="getSignatureStyle(sig)"
              @mousedown="onSignatureMouseDown($event, signatures.indexOf(sig), false)"
            >
              <img :src="sig.imageUrl" draggable="false" style="width: 100%; height: 100%; pointer-events: none; object-fit: contain;" />
              <div class="signature-handle">
                <v-icon small color="white">mdi-cursor-move</v-icon>
              </div>
              <div class="signature-number">
                {{ signatures.indexOf(sig) + 1 }}
              </div>
            </div>

            <!-- Firma digital draggable (única) -->
            <div
              v-if="currentPageDigitalSignature && digitalSignature.visible"
              class="signature-overlay digital-signature-overlay"
              :class="{ 'signature-selected': selectedSignatureIndex === null && isDragging }"
              :style="getSignatureStyle(digitalSignature)"
              @mousedown="onSignatureMouseDown($event, null, true)"
            >
              <img :src="digitalSignature.imageUrl" draggable="false" style="width: 100%; height: 100%; pointer-events: none; object-fit: contain;" />
              <div class="signature-handle" style="background: rgba(211, 47, 47, 0.8);">
                <v-icon small color="white">mdi-cursor-move</v-icon>
              </div>
              <div class="digital-badge">
                <v-icon small color="white">mdi-certificate</v-icon>
              </div>
            </div>
          </div>

          <!-- Instrucciones -->
          <v-alert v-if="pdfRendered" type="info" dense text class="mt-2">
            <div v-if="signatures.length === 0">
              Sube una imagen de firma y haz clic en "Agregar Firma" para colocarla en el PDF.
            </div>
            <div v-else>
              Arrastra las firmas para posicionarlas. Haz clic en una firma para seleccionarla y ajustar su tamaño.
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Mensajes -->
      <v-alert
        v-if="message"
        :type="alertType"
        dense
        text
        class="mt-4"
      >
        {{ message }}
      </v-alert>
    </v-card-text>

    <v-card-actions class="px-6 pb-6">
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        :disabled="!canSign"
        :loading="isSigning"
        @click="signDocument"
        large
      >
        <v-icon left>mdi-draw-pen</v-icon>
        {{ isSigning ? 'Procesando…' : (signatureType === 'electronic' ? 'Generar PDF con Firmas' : 'Firmar con DNIe') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Configurar worker de PDF.js (versión 2.16.105 compatible)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

export default {
  name: 'SignatureDNIeEditor',
  data() {
    const base = process.env.BASE_URL || '/'; 
    return {
      selectedFile: null,
      signatureImage: null,
      signatureImageUrl: null,
      signatureTab: 0,
      isSigning: false,
      message: '',
      dni: '',
      alertType: 'info',
      signatureType: 'electronic', // 'electronic' o 'digital'
      
      // PDF rendering
      pdfDoc: null,
      pdfJsDoc: null,
      pdfRendered: false,
      currentPage: 1,
      totalPages: 0,
      renderTask: null,
      
      // (configurable más adelante desde variables de entorno o build)
      msiDownloadUrl: `${base}static/downloads/SunExpert.DniSigner.Setup.msi`,
      
      // Múltiples firmas electrónicas
      signatures: [],
      selectedSignatureIndex: null,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      pageInput: 1,
      
      // Firma digital visual (única)
      digitalSignature: null,
      digitalSignatureImageUrl: null,
    };
  },
  computed: {
    canSign() {
      const hasElectronicSignatures = this.signatures.length > 0;
      const hasDigitalSignature = this.digitalSignature !== null;

      if (!this.selectedFile || !this.pdfRendered || this.isSigning) return false;

      // Para firma digital, el DNI es obligatorio
      if (this.signatureType === 'digital') {
        if (!hasDigitalSignature) return false;
        if (!this.dni || !this.dni.trim()) return false;
        return true;
      }

      // Para firma electrónica basta tener al menos una firma
      return hasElectronicSignatures;
    },
    selectedSignature() {
      return this.selectedSignatureIndex !== null ? this.signatures[this.selectedSignatureIndex] : null;
    },
    currentPageSignatures() {
      return this.signatures.filter(sig => sig.page === this.currentPage);
    },
    currentPageDigitalSignature() {
      return this.digitalSignature && this.digitalSignature.page === this.currentPage ? this.digitalSignature : null;
    },
  },
  methods: {
    async onPdfChange() {
      this.message = '';
      this.pdfRendered = false;
      this.pdfDoc = null;
      this.pdfJsDoc = null;
      this.signatures = [];
      this.selectedSignatureIndex = null;

      if (this.selectedFile) {
        try {
          await this.renderPdf();
        } catch (error) {
          console.error('Error cargando PDF:', error);
          this.message = 'Error al cargar el PDF: ' + error.message;
          this.alertType = 'error';
        }
      }
    },

    onSignatureChange() {
      if (this.signatureImageUrl) {
        window.URL.revokeObjectURL(this.signatureImageUrl);
        this.signatureImageUrl = null;
      }
      
      if (this.signatureImage) {
        this.signatureImageUrl = window.URL.createObjectURL(this.signatureImage);
      }
    },

    handleDrawnSignature(imageDataUrl) {
      // Convertir data URL a Blob
      fetch(imageDataUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], "firma_dibujada.png", { type: "image/png" });
          this.signatureImage = file;
          this.onSignatureChange();
          this.signatureTab = 0; // Volver a tab de imagen
        });
    },

    async generateDigitalSignatureSeal() {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        
        // Tamaño del canvas (ancho x alto) - similar a la imagen
        canvas.width = 550;
        canvas.height = 100;
        
        // Fondo blanco con borde
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);
        
        // Cargar escudo de Perú/RENIEC
        const escudoImg = new Image();
        escudoImg.crossOrigin = 'anonymous';
        escudoImg.onload = () => {
          // Dibujar escudo a la izquierda
          ctx.drawImage(escudoImg, 15, 10, 80, 80);
          
          // Sección izquierda - REPUBLICA DEL PERU / Firma Digital
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 10px Arial';
          ctx.fillText('REPUBLICA', 105, 25);
          ctx.fillText('DEL PERU', 105, 38);
          
          ctx.fillStyle = '#D32F2F';
          ctx.font = 'bold 12px Arial';
          ctx.fillText('Firma Digital', 105, 55);
          
          // Línea vertical separadora
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(200, 10);
          ctx.lineTo(200, 90);
          ctx.stroke();
          
          // Sección derecha - Información de firma
          ctx.fillStyle = '#000000';
          ctx.font = '11px Arial';
          ctx.fillText('Firmado digitalmente por:', 215, 20);
          
          // Nombre - usar DNI del usuario o ejemplo
          ctx.font = 'bold 12px Arial';
          const nombre = this.dni && this.dni.trim() 
            ? `USUARIO ${this.dni.trim().toUpperCase()}`
            : 'CHUMACERO JIMENEZ ELDER JOSE';
          ctx.fillText(nombre, 215, 38);
          
          // DNI
          ctx.font = '10px Arial';
          const dniText = this.dni && this.dni.trim()
            ? `FIR ${this.dni.trim()} hard`
            : 'FIR 75989315 hard';
          ctx.fillText(dniText, 215, 54);
          
          // Motivo
          ctx.fillText('Motivo: En señal de conformidad', 215, 68);
          
          // Fecha y hora actual
          const now = new Date();
          const dateStr = now.toLocaleDateString('es-PE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          });
          const timeStr = now.toLocaleTimeString('es-PE', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: false
          });
          ctx.fillText(`Fecha: ${dateStr} ${timeStr}-0500`, 215, 84);
          
          // Convertir canvas a blob
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/png');
        };
        
        escudoImg.onerror = () => {
          // Si falla cargar el escudo, dibujar sin él
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 10px Arial';
          ctx.fillText('REPUBLICA', 20, 25);
          ctx.fillText('DEL PERU', 20, 38);
          
          ctx.fillStyle = '#D32F2F';
          ctx.font = 'bold 12px Arial';
          ctx.fillText('Firma Digital', 20, 55);
          
          // Resto del contenido
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(200, 10);
          ctx.lineTo(200, 90);
          ctx.stroke();
          
          ctx.fillStyle = '#000000';
          ctx.font = '11px Arial';
          ctx.fillText('Firmado digitalmente por:', 215, 20);
          
          ctx.font = 'bold 12px Arial';
          const nombre = this.dni && this.dni.trim() 
            ? `USUARIO ${this.dni.trim().toUpperCase()}`
            : 'CHUMACERO JIMENEZ ELDER JOSE';
          ctx.fillText(nombre, 215, 38);
          
          ctx.font = '10px Arial';
          const dniText = this.dni && this.dni.trim()
            ? `FIR ${this.dni.trim()} hard`
            : 'FIR 75989315 hard';
          ctx.fillText(dniText, 215, 54);
          ctx.fillText('Motivo: En señal de conformidad', 215, 68);
          
          const now = new Date();
          const dateStr = now.toLocaleDateString('es-PE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          });
          const timeStr = now.toLocaleTimeString('es-PE', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: false
          });
          ctx.fillText(`Fecha: ${dateStr} ${timeStr}-0500`, 215, 84);
          
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/png');
        };
        
        // URL del escudo de Perú (puedes cambiarla por una local)
        escudoImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI0RERCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+RVNDVURPPC90ZXh0Pjwvc3ZnPic7'      });
    },


    async renderPdf() {
      const arrayBuffer = await this.selectedFile.arrayBuffer();
      
      // Cargar con pdf-lib para edición
      this.pdfDoc = await PDFDocument.load(arrayBuffer);
      this.totalPages = this.pdfDoc.getPageCount();
      this.currentPage = 1;
      this.pageInput = 1;
      
      // Cargar con pdf.js para renderizado
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      this.pdfJsDoc = await loadingTask.promise;
      
      await this.renderCurrentPage();
    },

    async renderCurrentPage() {
      if (!this.pdfJsDoc) return;

      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return;

      // Si hay un render en curso, lo cancelamos para evitar el error de pdf.js
      if (this.renderTask && typeof this.renderTask.cancel === 'function') {
        try {
          this.renderTask.cancel();
        } catch (e) {
          // ignorar
        }
      }

      const page = await this.pdfJsDoc.getPage(this.currentPage);
      const viewport = page.getViewport({ scale: 1 });

      // Escalar para que quepa en el contenedor
      const containerWidth = this.$refs.pdfContainer?.clientWidth || 800;
      const scale = Math.min(containerWidth / viewport.width, 1.5);
      
      const scaledViewport = page.getViewport({ scale });

      const ctx = canvas.getContext('2d');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      // Renderizar página PDF
      const renderContext = {
        canvasContext: ctx,
        viewport: scaledViewport,
      };

      const task = page.render(renderContext);
      this.renderTask = task;

      try {
        await task.promise;
        this.pdfRendered = true;
      } catch (e) {
        // Si la excepción es por cancelación, la ignoramos
        if (!e || e.name !== 'RenderingCancelledException') {
          throw e;
        }
      } finally {
        if (this.renderTask === task) {
          this.renderTask = null;
        }
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.pageInput = this.currentPage;
        await this.renderCurrentPage();
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.pageInput = this.currentPage;
        await this.renderCurrentPage();
      }
    },

    async goToPage() {
      let page = parseInt(this.pageInput);
      if (isNaN(page) || page < 1) {
        page = 1;
      } else if (page > this.totalPages) {
        page = this.totalPages;
      }
      this.pageInput = page;
      if (this.currentPage !== page) {
        this.currentPage = page;
        await this.renderCurrentPage();
      }
    },

    async addSignature() {
      if (!this.signatureImageUrl) return;

      const canvas = this.$refs.pdfCanvas;
      const centerX = (canvas.width / 2) - 100;
      const centerY = (canvas.width / 2) - 50;

      this.signatures.push({
        imageUrl: this.signatureImageUrl,
        imageFile: this.signatureImage,
        x: centerX,
        y: centerY,
        width: 200,
        height: 100,
        page: this.currentPage,
        rotation: 0,
        type: 'electronic',
      });

      this.selectedSignatureIndex = this.signatures.length - 1;
    },

    async addDigitalSignatureSeal() {
      if (this.digitalSignature) {
        this.message = 'Ya existe un sello digital. Elimina el actual para agregar uno nuevo.';
        this.alertType = 'warning';
        return;
      }

      const digitalBlob = await this.generateDigitalSignatureSeal();
      if (!digitalBlob) return;

      const digitalFile = new File([digitalBlob], "sello_digital.png", { type: "image/png" });
      const digitalUrl = window.URL.createObjectURL(digitalBlob);

      const canvas = this.$refs.pdfCanvas;
      const centerX = (canvas.width / 2) - 150;
      const centerY = (canvas.height / 2) - 50;

      this.digitalSignature = {
        imageUrl: digitalUrl,
        imageFile: digitalFile,
        x: centerX,
        y: centerY,
        width: 275,
        height: 50,
        page: this.currentPage,
        rotation: 0,
        type: 'digital',
        visible: true,
      };

      this.digitalSignatureImageUrl = digitalUrl;
      this.selectedSignatureIndex = null;
    },

    removeDigitalSignature() {
      if (this.digitalSignatureImageUrl) {
        window.URL.revokeObjectURL(this.digitalSignatureImageUrl);
      }
      this.digitalSignature = null;
      this.digitalSignatureImageUrl = null;
    },

    toggleDigitalSignatureVisibility() {
      if (this.digitalSignature) {
        this.digitalSignature.visible = !this.digitalSignature.visible;
      }
    },

    duplicateSignature(index) {
      const original = this.signatures[index];
      this.signatures.push({
        ...original,
        x: original.x + 20,
        y: original.y + 20,
        rotation: original.rotation || 0,
      });
      this.selectedSignatureIndex = this.signatures.length - 1;
    },

    removeSignature(index) {
      this.signatures.splice(index, 1);
      if (this.selectedSignatureIndex === index) {
        this.selectedSignatureIndex = null;
      } else if (this.selectedSignatureIndex > index) {
        this.selectedSignatureIndex--;
      }
    },

    async selectSignature(index) {
      this.selectedSignatureIndex = index;
      const sig = this.signatures[index];
      if (sig.page !== this.currentPage) {
        this.currentPage = sig.page;
        await this.renderCurrentPage();
      }
    },

    getSignatureStyle(sig) {
      return {
        left: `${sig.x}px`,
        top: `${sig.y}px`,
        width: `${sig.width}px`,
        height: `${sig.height}px`,
        cursor: this.isDragging ? 'grabbing' : 'grab',
        transform: `rotate(${sig.rotation || 0}deg)`,
      };
    },

    onSignatureMouseDown(event, index, isDigital = false) {
      this.isDragging = true;
      
      if (isDigital) {
        this.selectedSignatureIndex = null;
        const sig = this.digitalSignature;
        
        const container = this.$refs.pdfContainer;
        const rect = container.getBoundingClientRect();
        
        this.dragStartX = event.clientX - rect.left - sig.x;
        this.dragStartY = event.clientY - rect.top - sig.y;
      } else {
        this.selectedSignatureIndex = index;
        const sig = this.signatures[index];
        
        const container = this.$refs.pdfContainer;
        const rect = container.getBoundingClientRect();
        
        this.dragStartX = event.clientX - rect.left - sig.x;
        this.dragStartY = event.clientY - rect.top - sig.y;
      }
      
      event.preventDefault();
      event.stopPropagation();
    },

    onMouseMove(event) {
      if (!this.isDragging) return;

      const container = this.$refs.pdfContainer;
      const rect = container.getBoundingClientRect();
      const canvas = this.$refs.pdfCanvas;
      
      let newX = event.clientX - rect.left - this.dragStartX;
      let newY = event.clientY - rect.top - this.dragStartY;

      // Determinar si estamos moviendo firma electrónica o digital
      if (this.selectedSignatureIndex !== null) {
        const sig = this.signatures[this.selectedSignatureIndex];
        newX = Math.max(0, Math.min(newX, canvas.width - sig.width));
        newY = Math.max(0, Math.min(newY, canvas.height - sig.height));
        sig.x = newX;
        sig.y = newY;
      } else if (this.digitalSignature && this.isDragging) {
        const sig = this.digitalSignature;
        newX = Math.max(0, Math.min(newX, canvas.width - sig.width));
        newY = Math.max(0, Math.min(newY, canvas.height - sig.height));
        sig.x = newX;
        sig.y = newY;
      }
    },

    onMouseUp() {
      this.isDragging = false;
    },

    async ensureDniHostRunning() {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 1500); // 1.5s timeout

      try {
        const res = await fetch('http://127.0.0.1:8733/ping', {
          method: 'GET',
          signal: controller.signal,
        });
        clearTimeout(timeout);
        return res && res.ok;
      } catch (e) {
        clearTimeout(timeout);
        return false;
      }
    },

    async signDocument() {
      if (!this.canSign) {
        if (this.signatureType === 'digital' && (!this.dni || !this.dni.trim())) {
          this.message = 'Debe ingresar el DNI para realizar la firma digital con DNIe.';
          this.alertType = 'error';
        }
        return;
      }

      try {
        this.isSigning = true;
        this.message = 'Insertando firmas en el PDF...';
        this.alertType = 'info';

        // Antes de seguir, si es firma digital comprobamos que el host local esté levantado
        if (this.signatureType === 'digital') {
          const ok = await this.ensureDniHostRunning();
          if (!ok) {
            this.isSigning = false;
            this.message = 'No se encontró el componente local de firma DNIe. Instale el componente y vuelva a intentarlo.';
            this.alertType = 'error';

            // Si se ha configurado una URL de descarga del MSI, la abrimos en una nueva pestaña
            if (this.msiDownloadUrl) {
              window.open(this.msiDownloadUrl, '_blank');
            }
            return;
          }
        }

        // Cargar PDF con pdf-lib
        const arrayBuffer = await this.selectedFile.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        // Preparar coordenadas separadas para el backend
        const electronicSignatures = [];
        let digitalSignatureData = null;

        // Insertar firmas electrónicas
        for (const sig of this.signatures) {
          const signatureBytes = await sig.imageFile.arrayBuffer();
          const signatureImageEmbed = sig.imageFile.type === 'image/png'
            ? await pdfDoc.embedPng(signatureBytes)
            : await pdfDoc.embedJpg(signatureBytes);

          const page = pdfDoc.getPage(sig.page - 1);
          const { width: pageWidth, height: pageHeight } = page.getSize();

          const canvas = this.$refs.pdfCanvas;
          const scaleX = pageWidth / canvas.width;
          const scaleY = pageHeight / canvas.height;

          const pdfX = sig.x * scaleX;
          const pdfY = pageHeight - (sig.y * scaleY) - (sig.height * scaleY);
          const pdfWidth = sig.width * scaleX;
          const pdfHeight = sig.height * scaleY;
          const rotation = sig.rotation || 0;

          page.drawImage(signatureImageEmbed, {
            x: pdfX,
            y: pdfY,
            width: pdfWidth,
            height: pdfHeight,
            rotate: { angle: rotation, type: 'degrees' },
          });

          electronicSignatures.push({
            page: sig.page,
            x: pdfX,
            y: pdfY,
            width: pdfWidth,
            height: pdfHeight,
            rotation: rotation,
          });
        }

        // Firma digital: SOLO usamos el rectángulo como referencia para la firma
        // criptográfica. NO insertamos la imagen en el PDF; el sello visual es solo
        // para que el usuario elija la posición.
        if (this.digitalSignature) {
          const sig = this.digitalSignature;
          const page = pdfDoc.getPage(sig.page - 1);
          const { width: pageWidth, height: pageHeight } = page.getSize();

          const canvas = this.$refs.pdfCanvas;
          const scaleX = pageWidth / canvas.width;
          const scaleY = pageHeight / canvas.height;

          const pdfX = sig.x * scaleX;
          const pdfY = pageHeight - (sig.y * scaleY) - (sig.height * scaleY);
          const pdfWidth = sig.width * scaleX;
          const pdfHeight = sig.height * scaleY;
          const rotation = sig.rotation || 0;

          digitalSignatureData = {
            page: sig.page,
            x: pdfX,
            y: pdfY,
            width: pdfWidth,
            height: pdfHeight,
            rotation: rotation,
            visible: !!sig.visible,
          };
        }

        // Guardar PDF modificado
        const pdfBytes = await pdfDoc.save();

        // Determinar endpoint según tipo de firma
        const endpoint = this.signatureType === 'electronic' 
          ? 'http://127.0.0.1:8733/sign-electronic'
          : 'http://127.0.0.1:8733/sign-digital';

        // Enviar al backend
        this.message = this.signatureType === 'electronic'
          ? 'Generando PDF con firmas electrónicas...'
          : 'Enviando al componente de firma DNIe...';
        
        const params = new URLSearchParams();
        if (this.dni && this.dni.trim() !== '') {
          params.append('dni', this.dni.trim());
        }
        
        const query = params.toString();
        const url = endpoint + (query ? '?' + query : '');

        // Preparar headers con coordenadas separadas
        const headers = {
          'Content-Type': 'application/pdf',
        };

        if (electronicSignatures.length > 0) {
          headers['X-Electronic-Signatures'] = JSON.stringify(electronicSignatures);
        }

        if (digitalSignatureData) {
          headers['X-Digital-Signature'] = JSON.stringify(digitalSignatureData);
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: headers,
          body: pdfBytes
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Error al ${this.signatureType === 'electronic' ? 'generar PDF' : 'firmar con DNIe'}`);
        }

        const signedBuffer = await response.arrayBuffer();
        const signedBlob = new Blob([signedBuffer], { type: 'application/pdf' });

        // Descargar
        const urlResponse = window.URL.createObjectURL(signedBlob);
        const a = document.createElement('a');
        const baseName = this.selectedFile.name.replace(/\.pdf$/i, '');
        const suffix = this.signatureType === 'electronic' ? '_electronic' : '_signed';
        a.href = urlResponse;
        a.download = baseName + suffix + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlResponse);

        const actionText = this.signatureType === 'electronic' 
          ? 'PDF con firmas electrónicas generado correctamente'
          : 'Firma digital con DNIe generada correctamente';
        
        const totalSigs = electronicSignatures.length + (digitalSignatureData && digitalSignatureData.visible ? 1 : 0);
        this.message = `${actionText}. ${totalSigs} firma(s) insertada(s).`;
        this.alertType = 'success';
        
        console.log('Tipo de firma:', this.signatureType);
        console.log('Firmas electrónicas:', electronicSignatures);
        console.log('Firma digital:', digitalSignatureData);
      } catch (err) {
        console.error(err);
        this.message = 'Error: ' + (err?.message || err);
        this.alertType = 'error';
      } finally {
        this.isSigning = false;
      }
    },
  },

  beforeDestroy() {
    if (this.signatureImageUrl) {
      window.URL.revokeObjectURL(this.signatureImageUrl);
    }
  },
};
</script>

<style scoped>
.pdf-container {
  position: relative;
  border: 2px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f5f5;
  min-height: 400px;
}

.signature-overlay {
  position: absolute;
  border: 2px dashed #1976d2;
  background: rgba(25, 118, 210, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  user-select: none;
  transition: all 0.2s;
}

.signature-overlay:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border-color: #1565c0;
}

.signature-selected {
  border-color: #ff9800 !important;
  border-width: 3px !important;
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.4) !important;
}

.signature-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(25, 118, 210, 0.8);
  padding: 4px;
  border-radius: 50%;
  pointer-events: none;
  opacity: 0.7;
}

.signature-number {
  position: absolute;
  top: -10px;
  right: -10px;
  background: #1976d2;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  pointer-events: none;
}

.selected-signature {
  background: rgba(255, 152, 0, 0.1);
}

.digital-signature-overlay {
  border-color: #D32F2F !important;
  background: rgba(211, 47, 47, 0.1);
}

.digital-signature-overlay:hover {
  border-color: #B71C1C !important;
}

.digital-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background: #D32F2F;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

canvas {
  display: block;
  max-width: 100%;
}
</style>
